import React, { useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { Form, withFormik } from 'formik';
import * as Yup from 'yup';

import { IconBtn, SimpleCenteredModal } from '../../../components';
import { StepMap } from '../../../components/Shared';
import {
  InsuranceEndorsementFormAdd,
  InsuranceEndorsementFormCut,
  InsuranceEndorsementFormFooter,
  InsuranceEndorsementFormHeader,
  InsuranceEndorsementFormModification,
  InsuranceEndorsementFormModify,
  InsuranceEndorsementFormType
} from './Form';
import InsuranceEndorsementSecondStep from './Form/InsuranceEndorsementSecondStep';
import { insuranceItemSchemaByCase } from './Form/InsuranceItemAttributesSchemas';
import InsuranceEndorsementDocumentForm from './Form/InsuranceEndorsementDocumentForm';

const AdminInsuranceEndorsementForm = ({
  action,
  errors,
  isSubmitting,
  setFieldValue,
  touched,
  values,
  isValid,
  handleSubmit,
  validateForm,
  ...props
}) => {
  const [showModal, setShowModal] = useState(false);
  const modelName = 'insuranceEndorsement';

  const { endorsementAction, endorsementType, insuranceItemId, originalInsuranceItem } = values.insuranceEndorsement;
  const { insurancePolicy, originPath } = props;

  const isFormModification = endorsementType === 'modification' && !endorsementAction && insuranceItemId;
  const isFormAdd = endorsementAction === 'add';
  const isRemove = endorsementAction === 'remove' && insuranceItemId;
  const applyFormCut = { cut: 'Corte', extension: 'Prórroga' };
  const isFormCut = (isRemove && 'Eliminación') || applyFormCut[endorsementType] || applyFormCut[endorsementAction];
  const isFormModify = endorsementAction === 'modify' && !isFormCut && insuranceItemId;

  const [step, setStep] = useState(1);
  const isFirstStep = step === 1;
  const isLastStep = step === 2;

  const proposalMessage = action === 'edit' ? 'Editar propuesta' : 'Crear propuesta';
  const submitText = isLastStep ? proposalMessage : 'Siguiente';

  const toggleStep = ({ back = false }) => {
    const limitStep = back ? 1 : 2;
    if (isFirstStep) {
      validateForm().then(() => {
        if (isValid) setStep(limitStep);
      });
      return;
    }
    setStep(limitStep);
  };

  return (
    <Form>
      <StepMap steps={[1, 2]} currentStep={step} />
      {step === 1 && (
        <>
          <InsuranceEndorsementFormHeader insurancePolicy={insurancePolicy} />
          <InsuranceEndorsementFormType modelName={modelName} insurancePolicy={insurancePolicy} />
          {isFormCut && (
            <InsuranceEndorsementFormCut insurancePolicy={insurancePolicy} dateLabel={isFormCut} isRemove={isRemove} />
          )}
          {isFormModification && (
            <InsuranceEndorsementFormModification modelName={modelName} insurancePolicy={insurancePolicy} />
          )}
          {isFormModify && (
            <InsuranceEndorsementFormModify
              originalInsuranceItem={originalInsuranceItem}
              insurancePolicy={insurancePolicy}
            />
          )}
          {isFormAdd && <InsuranceEndorsementFormAdd modelName={modelName} insurancePolicy={insurancePolicy} />}
          {(isFormCut || isFormModification || isFormModify || isFormAdd) && (
            <InsuranceEndorsementFormFooter modelName={modelName} />
          )}
        </>
      )}
      {step === 2 && (
        <InsuranceEndorsementSecondStep
          insurancePolicy={insurancePolicy}
          isFormCut={isFormCut}
          modelName={modelName}
          DocumentModal={() => (
            <SimpleCenteredModal
              title="Documento de endoso"
              show={showModal}
              body={
                <InsuranceEndorsementDocumentForm
                  modelName={modelName}
                  insurancePolicy={insurancePolicy}
                  onHide={() => setShowModal(false)}
                />
              }
              onHide={() => setShowModal(false)}
            />
          )}
        />
      )}
      <Row className="mt-5">
        <Col md={4} lg={3} className="mt-4">
          {!isFirstStep && (
            <Button block variant="cancel" onClick={() => toggleStep({ back: true })}>
              Volver
            </Button>
          )}
        </Col>
        <Col md={8} lg={9}>
          <Row className="d-flex flex-row-reverse align-items-center">
            <Col md={6} lg={4} className="mt-4">
              <Button
                disabled={!endorsementType || !isValid || isSubmitting}
                block
                variant="submit"
                onClick={isLastStep ? () => setShowModal(true) : toggleStep}
              >
                {submitText}
              </Button>
            </Col>
            <Col md={5} lg={3} className="mt-4">
              <IconBtn block variant="cancel" to={originPath}>
                Cancelar
              </IconBtn>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

const setInitialValues = ({ insuranceEndorsement }) => ({ insuranceEndorsement });

const validationSchema = Yup.object().shape({
  insuranceEndorsement: Yup.object().shape({
    approval: Yup.mixed().required('Debes adjuntar una aprobacion'),
    comments: Yup.string().nullable(),
    endorsementAction: Yup.string().nullable(),
    endorsementDate: Yup.string().when('endorsementType', {
      is: currentType => ['cut', 'decrease', 'increase', 'modification'].includes(currentType),
      then: Yup.string().required('Debes ingresar una fecha'),
      otherwise: Yup.string().nullable()
    }),
    endorsementType: Yup.string().required('Debes ingresar un tipo'),
    insuranceItemId: Yup.string().when('endorsementType', {
      is: currentType => ['decrease', 'modification'].includes(currentType),
      then: Yup.string().required('Debes seleccionar un ítem'),
      otherwise: Yup.string().nullable()
    }),
    insuranceItemAttributes: Yup.object().when(
      ['endorsementType', 'endorsementAction'],
      (endorsementType, endorsementAction, insuranceItemAttributesSchema) =>
        insuranceItemSchemaByCase({ endorsementType, endorsementAction, insuranceItemAttributesSchema })
    )
  })
});

const handleSubmit = (values, { props, setSubmitting }) => {
  const { formRequest, onHide } = props;
  formRequest(values, setSubmitting);
  if (onHide) onHide();
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  handleSubmit,
  validationSchema,
  enableReinitialize: true,
  validateOnMount: props => props.action !== 'new'
})(AdminInsuranceEndorsementForm);
